(function attachCombinedProductModel(globalScope, factory) {
  const api = factory();

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  if (globalScope) {
    globalScope.CombinedProductModel = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function createCombinedProductModel() {
  function normalizeCombinedProductData(rawData) {
    if (!rawData || !Array.isArray(rawData.families)) return null;

    const moneyFormat = rawData.moneyFormat || '${{amount}}';
    const labels = rawData.labels || {};
    const normalizedFamilies = rawData.families
      .map((family) => normalizeFamily(family, { moneyFormat }))
      .filter(Boolean);

    if (!normalizedFamilies.length) return null;

    return {
      moneyFormat,
      labels,
      destination: normalizeDestination(rawData, normalizedFamilies[0]),
      families: normalizedFamilies,
    };
  }

  function normalizeDestination(rawData, primaryFamily) {
    const destination = rawData.destination || {};
    const primaryImage = primaryFamily && primaryFamily.featuredImage;

    return {
      title: destination.title || (primaryFamily ? primaryFamily.destinationTitle : ''),
      subtitle:
        destination.subtitle ||
        defaultDestinationSubtitle(destination.title || (primaryFamily ? primaryFamily.destinationTitle : '')),
      image: destination.image || (primaryImage ? primaryImage.src : ''),
      imageAlt: destination.imageAlt || (primaryImage ? primaryImage.alt : ''),
    };
  }

  function defaultDestinationSubtitle(destinationTitle) {
    const tripTarget = normalizeText(destinationTitle);
    const destinationPhrase = tripTarget ? `${tripTarget} trip` : 'trip';

    return `Choose standard or unlimited data for your ${destinationPhrase}. Install before you fly, avoid roaming fees, and connect when you land.`;
  }

  function normalizeFamily(family, context) {
    if (!family || !family.product || !Array.isArray(family.product.variants)) return null;

    const packages = family.product.variants.map((variant) => normalizePackage(variant, family.key, context.moneyFormat));
    packages.sort(sortPackages);

    const preferredPackage = getPreferredPackage(packages, family.selectedVariantId);
    const durations = normalizeDurations(packages);

    return {
      key: family.key,
      label: family.label,
      description: family.description,
      modalKey: family.modalKey,
      formId: family.formId,
      productId: family.product.id,
      handle: family.product.handle,
      productTitle: family.product.title || '',
      destinationTitle: normalizeDestinationTitle(family.product.title || ''),
      featuredImage: normalizeFeaturedImage(family.product.featured_image),
      selectedVariantId: preferredPackage ? String(preferredPackage.variantId) : null,
      selectedDurationKey: preferredPackage ? preferredPackage.durationKey : null,
      packages,
      durations,
      details: normalizeDetails(family.product, {
        destinationTitle: normalizeDestinationTitle(family.product.title || ''),
      }),
    };
  }

  function normalizeFeaturedImage(featuredImage) {
    if (!featuredImage) return null;

    return {
      src: featuredImage.src || featuredImage.url || '',
      alt: featuredImage.alt || '',
    };
  }

  function normalizePackage(variant, familyKey, moneyFormat) {
    const duration = extractDuration(variant);
    const dataDetails = extractDataLabel(variant, familyKey);
    const priceCents = Number(variant.price || 0);
    const available = Boolean(variant.available);
    const priceLabel = normalizeText(variant.priceLabel) || formatMoney(priceCents, moneyFormat);

    return {
      variantId: String(variant.id),
      available,
      durationKey: duration.key,
      durationLabel: duration.label,
      durationSort: duration.sort,
      dataKey: dataDetails.key,
      dataLabel: dataDetails.label,
      dataSort: dataDetails.sort,
      cardTitle: dataDetails.cardTitle,
      cardSupportingLine: dataDetails.cardSupportingLine,
      summaryLabel: dataDetails.summary,
      summaryHelperText: dataDetails.summaryHelperText,
      packageTitle: dataDetails.packageTitle,
      packageEyebrow: dataDetails.eyebrow,
      modalAllowanceCopy: dataDetails.modalAllowanceCopy,
      priceCents,
      priceLabel,
      sourceVariant: {
        id: variant.id,
        title: variant.title || '',
        option1: variant.option1 || '',
        option2: variant.option2 || '',
        option3: variant.option3 || '',
      },
    };
  }

  function normalizeDurations(packages) {
    const durationMap = new Map();

    packages.forEach((pkg) => {
      if (!durationMap.has(pkg.durationKey)) {
        durationMap.set(pkg.durationKey, {
          key: pkg.durationKey,
          label: pkg.durationLabel,
          sort: pkg.durationSort,
          hasAvailable: pkg.available,
        });
        return;
      }

      const existing = durationMap.get(pkg.durationKey);
      existing.hasAvailable = existing.hasAvailable || pkg.available;
    });

    return Array.from(durationMap.values()).sort((left, right) => left.sort - right.sort);
  }

  function normalizeDetails(product, context) {
    const structured = extractStructuredContent(product && product.description);
    const rawFallbackHtml = sanitizeProductDescription(product && product.description);
    const destinationTitle = normalizeText(context && context.destinationTitle);

    return {
      overviewHtml: normalizeOverviewHtml(structured.overviewHtml, destinationTitle),
      features: structured.features,
      specs: structured.specs.filter((item) => !isPlaceholderValue(item.value)),
      rawFallbackHtml,
    };
  }

  function extractStructuredContent(description) {
    const wrapper = createDescriptionWrapper(description);
    if (!wrapper) {
      return {
        overviewHtml: '',
        features: [],
        specs: [],
      };
    }

    const overviewHtml = extractDescriptionMarkup(wrapper);
    const features = extractStructuredItems(wrapper, '.feature-item', 'feature').filter((item) => item.key !== 'compatibility');
    const specs = extractStructuredItems(wrapper, '.spec-item', 'spec');

    return {
      overviewHtml,
      features,
      specs,
    };
  }

  function createDescriptionWrapper(description) {
    if (!description || typeof document === 'undefined' || !document.createElement) return null;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = description;
    return wrapper;
  }

  function extractStructuredItems(wrapper, selector, dataAttribute) {
    return Array.from(wrapper.querySelectorAll(selector))
      .map((item) => {
        const clone = item.cloneNode(true);
        const labelNode = clone.querySelector('strong');
        const label = normalizeText(labelNode ? labelNode.textContent.replace(/:\s*$/, '') : '');

        if (labelNode) {
          labelNode.remove();
        }

        const value = normalizeText(clone.textContent);
        const key = normalizeText(item.dataset[dataAttribute] || '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');

        if (!label || !value || !key) return null;

        return {
          key,
          label,
          value,
        };
      })
      .filter(Boolean);
  }

  function extractDescriptionMarkup(wrapper) {
    const preferredSource = wrapper.querySelector('.box-description') || wrapper.querySelector('.more-description') || wrapper;
    const clone = preferredSource.cloneNode(true);

    clone.querySelectorAll('script, style, iframe, h1, h2, h3, h4, h5, h6, .feature-item, .spec-item').forEach((item) => {
      item.remove();
    });

    const paragraphs = Array.from(clone.querySelectorAll('p, ul, ol'))
      .map((item) => item.outerHTML)
      .join('');

    return sanitizeProductDescription(paragraphs || clone.innerHTML) || '';
  }

  function normalizeOverviewHtml(overviewHtml, destinationTitle) {
    if (!overviewHtml || !destinationTitle || isGenericRegionLabel(destinationTitle)) return overviewHtml;

    const wrapper = createDescriptionWrapper(overviewHtml);
    if (!wrapper) return overviewHtml;

    const firstParagraph = wrapper.querySelector('p');
    if (!firstParagraph) return overviewHtml;

    const firstText = normalizeText(firstParagraph.textContent);
    if (!isGenericRegionOverview(firstText, destinationTitle)) return overviewHtml;

    firstParagraph.textContent = `Get highest speeds in ${destinationTitle} with this eSIM plan. Start your eSIM instantly upon arrival in ${destinationTitle} and enjoy high-speed data with no hidden roaming fees.`;

    return sanitizeProductDescription(wrapper.innerHTML) || overviewHtml;
  }

  function sanitizeProductDescription(description) {
    if (!description || typeof document === 'undefined' || !document.createElement) return normalizeText(description);

    const wrapper = document.createElement('div');
    wrapper.innerHTML = description;

    wrapper.querySelectorAll('script, style, iframe').forEach((element) => element.remove());

    const placeholderPattern = /\bunknown\b|\btbd\b|\bcoming soon\b/i;
    wrapper.querySelectorAll('.box-description, .more-description, p').forEach((element) => {
      const text = normalizeText(element.textContent);
      if (text && placeholderPattern.test(text)) {
        element.remove();
      }
    });

    wrapper.querySelectorAll('a').forEach((link) => {
      const href = link.getAttribute('href') || '';
      if (/gsmarena/i.test(href)) {
        link.remove();
      }
    });

    const meaningfulText = normalizeText(wrapper.textContent);
    return meaningfulText ? wrapper.innerHTML.trim() : '';
  }

  function isPlaceholderValue(value) {
    const normalized = normalizeText(value).toLowerCase();
    return ['unknown', 'tbd', 'n/a', 'na', 'not available'].includes(normalized);
  }

  function renderDetailsMarkup(details, context) {
    if (!details) return '';

    const overviewHtml = resolveOverviewHtml(details, context);
    const hasStructuredContent = Boolean(overviewHtml || details.features.length || details.specs.length);
    const contentMarkup = hasStructuredContent
      ? `
        <div class="cp-description-content">
          <div class="three-box-layout">
            ${
              overviewHtml
                ? `
                  <div class="box box-description">
                    <h2>Plan overview</h2>
                    ${overviewHtml}
                  </div>
                `
                : ''
            }
            ${
              details.features.length
                ? `
                  <div class="box box-features">
                    <h2>Features</h2>
                    <ul class="spec-list">
                      ${renderStructuredList(details.features, 'feature')}
                    </ul>
                  </div>
                `
                : ''
            }
            ${
              details.specs.length
                ? `
                  <div class="box box-technical-specs">
                    <h2>Technical specs</h2>
                    <ul class="spec-list">
                      ${renderStructuredList(details.specs, 'spec')}
                    </ul>
                  </div>
                `
                : ''
            }
          </div>
        </div>
      `
      : details.rawFallbackHtml
        ? `<div class="cp-description-content">${details.rawFallbackHtml}</div>`
        : '';

    return contentMarkup;
  }

  function resolveOverviewHtml(details, context) {
    const overviewHtml = details && details.overviewHtml ? details.overviewHtml : '';
    const family = context && context.family;
    const selectedPackage = context && context.selectedPackage;

    if (!family || family.key !== 'unlimited') {
      return overviewHtml;
    }

    return buildUnlimitedOverviewHtml(overviewHtml, selectedPackage);
  }

  function buildUnlimitedOverviewHtml(overviewHtml, selectedPackage) {
    const dynamicText = normalizeText(selectedPackage && selectedPackage.modalAllowanceCopy) || buildUnlimitedAllowanceCopy();

    if (typeof document === 'undefined' || !document.createElement) {
      return `<p>${escapeHtml(dynamicText)}</p>`;
    }

    const wrapper = createDescriptionWrapper(overviewHtml || '<p></p>');
    if (!wrapper) {
      return `<p>${escapeHtml(dynamicText)}</p>`;
    }

    const paragraphs = Array.from(wrapper.querySelectorAll('p'));
    let injectedCopy = false;

    paragraphs.forEach((paragraph) => {
      const text = normalizeText(paragraph.textContent);
      if (!text || !mentionsLegacySpeedCopy(text)) return;

      paragraph.textContent = appendSentence(
        removeLegacySpeedCopy(text),
        dynamicText
      );
      injectedCopy = true;
    });

    if (!injectedCopy) {
      const targetParagraph = paragraphs[paragraphs.length - 1];

      if (targetParagraph) {
        targetParagraph.textContent = appendSentence(normalizeText(targetParagraph.textContent), dynamicText);
      } else {
        const paragraph = document.createElement('p');
        paragraph.textContent = dynamicText;
        wrapper.appendChild(paragraph);
      }
    }

    return sanitizeProductDescription(wrapper.innerHTML) || `<p>${escapeHtml(dynamicText)}</p>`;
  }

  function buildUnlimitedAllowanceCopy(allowanceLabel) {
    const normalizedAllowance = allowanceLabel || 'your selected daily high-speed allowance';
    return `Includes ${normalizedAllowance} of high-speed data each day. After the daily allowance is used, data stays unlimited at reduced speeds for the rest of the 24-hour period.`;
  }

  function mentionsLegacySpeedCopy(text) {
    return /\b2g\s+speeds?\b/i.test(text);
  }

  function removeLegacySpeedCopy(text) {
    return normalizeText(
      String(text || '')
        .replace(/(^|\s+)Unlimited\s+2G\s+speeds?\.?/gi, ' ')
        .replace(/(^|\s+)2G\s+speeds?\.?/gi, ' ')
        .replace(/\s+\./g, '.')
    );
  }

  function appendSentence(text, sentence) {
    const normalizedText = normalizeText(text);
    const normalizedSentence = normalizeText(sentence);

    if (!normalizedText) return normalizedSentence;
    if (!normalizedSentence) return normalizedText;
    if (normalizedText.includes(normalizedSentence)) return normalizedText;

    const suffix = /[.!?]$/.test(normalizedText) ? '' : '.';
    return `${normalizedText}${suffix} ${normalizedSentence}`.trim();
  }

  function renderStructuredList(items, type) {
    return items
      .map(
        (item) => `
          <li class="${type}-item" data-${type}="${escapeHtml(item.key)}">
            <strong>${escapeHtml(item.label)}:</strong>
            <span>${escapeHtml(item.value)}</span>
          </li>
        `
      )
      .join('');
  }

  function createCombinedProductState(model, requestedTab) {
    if (!model || !Array.isArray(model.families) || !model.families.length) return null;

    const activeFamily = resolveInitialFamily(model.families, requestedTab);

    return {
      ...model,
      activeFamilyKey: activeFamily.key,
    };
  }

  function resolveInitialFamily(families, requestedTab) {
    if (!requestedTab) return families[0];

    const normalizedTab = String(requestedTab).trim().toLowerCase();
    return (
      families.find((family) => family.key.toLowerCase() === normalizedTab) ||
      families.find((family) => family.label.toLowerCase().includes(normalizedTab)) ||
      families[0]
    );
  }

  function getPreferredPackage(packages, preferredId) {
    return (
      packages.find((pkg) => String(pkg.variantId) === String(preferredId) && pkg.available) ||
      packages.find((pkg) => pkg.available) ||
      packages[0] ||
      null
    );
  }

  function getActiveFamily(state) {
    return state.families.find((family) => family.key === state.activeFamilyKey) || state.families[0] || null;
  }

  function getSelectedPackage(family) {
    if (!family) return null;

    return (
      family.packages.find((pkg) => String(pkg.variantId) === String(family.selectedVariantId)) ||
      getPreferredPackage(family.packages, family.selectedVariantId)
    );
  }

  function getPackagesForDuration(family, durationKey) {
    return family.packages.filter((pkg) => pkg.durationKey === durationKey);
  }

  function ensureActiveSelection(state) {
    const activeFamily = getActiveFamily(state);
    if (!activeFamily) return null;

    const selectedPackage = getSelectedPackage(activeFamily);
    if (!selectedPackage) return null;

    activeFamily.selectedVariantId = String(selectedPackage.variantId);
    activeFamily.selectedDurationKey = selectedPackage.durationKey;
    return selectedPackage;
  }

  function setActiveFamily(state, familyKey) {
    if (!state || state.activeFamilyKey === familyKey) return getActiveFamily(state);

    state.activeFamilyKey = familyKey;
    const activeFamily = getActiveFamily(state);
    const preferredPackage = getPreferredPackage(activeFamily.packages, activeFamily.selectedVariantId);

    if (preferredPackage) {
      activeFamily.selectedVariantId = String(preferredPackage.variantId);
      activeFamily.selectedDurationKey = preferredPackage.durationKey;
    }

    return activeFamily;
  }

  function setActiveDuration(state, durationKey) {
    const activeFamily = getActiveFamily(state);
    if (!activeFamily || activeFamily.selectedDurationKey === durationKey) return getSelectedPackage(activeFamily);

    activeFamily.selectedDurationKey = durationKey;
    const durationPackages = getPackagesForDuration(activeFamily, durationKey);
    const currentPackage = getSelectedPackage(activeFamily);
    const nextPackage =
      durationPackages.find((pkg) => String(pkg.variantId) === String(currentPackage && currentPackage.variantId)) ||
      durationPackages.find((pkg) => pkg.available) ||
      durationPackages[0] ||
      null;

    if (nextPackage) {
      activeFamily.selectedVariantId = String(nextPackage.variantId);
    }

    return nextPackage;
  }

  function setSelectedPackage(state, variantId) {
    const activeFamily = getActiveFamily(state);
    if (!activeFamily) return null;

    const nextPackage = activeFamily.packages.find((pkg) => String(pkg.variantId) === String(variantId));
    if (!nextPackage) return null;

    activeFamily.selectedVariantId = String(nextPackage.variantId);
    activeFamily.selectedDurationKey = nextPackage.durationKey;
    return nextPackage;
  }

  function extractDuration(variant) {
    const tokens = collectVariantTokens(variant);

    for (const token of tokens) {
      const match = token.match(/(\d+)\s*Days?/i);
      if (match) {
        const value = Number(match[1]);
        return { key: String(value), label: formatDays(value), sort: value };
      }

      if (/^\d+$/.test(token.trim())) {
        const value = Number(token.trim());
        return { key: String(value), label: formatDays(value), sort: value };
      }
    }

    const slashParts = String(variant.title || '')
      .split('/')
      .map((part) => part.trim())
      .filter(Boolean);

    const slashValue = slashParts.find((part) => /^\d+$/.test(part));
    if (slashValue) {
      const value = Number(slashValue);
      return { key: String(value), label: formatDays(value), sort: value };
    }

    return { key: 'unknown', label: 'Unknown validity', sort: 9999 };
  }

  function extractDataLabel(variant, familyKey) {
    const tokens = collectVariantTokens(variant);

    for (const token of tokens) {
      const dailyMatch =
        token.match(/(\d+(?:\.\d+)?)\s*(MB|GB)\s*\/\s*Day/i) ||
        token.match(/(\d+(?:\.\d+)?)\s*(MB|GB)\/Day/i);

      if (dailyMatch) {
        const value = dailyMatch[1];
        const unit = dailyMatch[2].toUpperCase();
        const label = `${value}${unit}/day`;
        const sort = Number(value) * (unit === 'GB' ? 1000 : 1);

        if (familyKey === 'unlimited') {
          return createUnlimitedDailyPackageCopy(label, sort);
        }

        return createPackageCopy({
          key: label,
          label,
          cardTitle: `${label} high speed`,
          summary: `${label} high speed`,
          packageTitle: `${label} high speed`,
          sort,
        });
      }
    }

    for (const token of tokens) {
      const dataMatch = token.match(/(\d+(?:\.\d+)?)\s*(MB|GB)/i);
      if (dataMatch) {
        const value = dataMatch[1];
        const unit = dataMatch[2].toUpperCase();
        const label = `${value}${unit}`;
        const sort = Number(value) * (unit === 'GB' ? 1000 : 1);

        return createPackageCopy({
          key: label,
          label,
          cardTitle: familyKey === 'unlimited' ? `${label} high speed` : label,
          summary: familyKey === 'unlimited' ? `${label} high speed` : label,
          packageTitle: familyKey === 'unlimited' ? `${label} high speed` : label,
          eyebrow: familyKey === 'unlimited' ? 'Unlimited' : null,
          sort,
        });
      }
    }

    const title = familyKey === 'unlimited' ? 'Unlimited data' : variant.title || 'Plan option';

    return createPackageCopy({
      key: title,
      label: title,
      cardTitle: title,
      summary: title,
      packageTitle: title,
      eyebrow: familyKey === 'unlimited' ? 'Unlimited' : null,
      sort: 999999,
    });
  }

  function createUnlimitedDailyPackageCopy(label, sort) {
    const summaryHelperText =
      'After the daily allowance is used, data stays unlimited at reduced speeds for the rest of the 24-hour period.';

    return createPackageCopy({
      key: label,
      label,
      cardTitle: label,
      cardSupportingLine: 'Unlimited data, reduced speeds after allowance',
      summary: `Unlimited data • ${label} daily high-speed allowance`,
      summaryHelperText,
      packageTitle: label,
      eyebrow: 'Unlimited',
      modalAllowanceCopy: `Includes ${label} of high-speed data each day. After the daily allowance is used, data stays unlimited at reduced speeds for the rest of the 24-hour period.`,
      sort,
    });
  }

  function createPackageCopy({
    key,
    label,
    cardTitle,
    cardSupportingLine = '',
    summary,
    summaryHelperText = '',
    packageTitle,
    eyebrow = null,
    modalAllowanceCopy = '',
    sort,
  }) {
    return {
      key,
      label,
      cardTitle: cardTitle || packageTitle || label,
      cardSupportingLine,
      summary,
      summaryHelperText,
      packageTitle: packageTitle || cardTitle || label,
      eyebrow,
      modalAllowanceCopy,
      sort,
    };
  }

  function collectVariantTokens(variant) {
    return Array.from(
      new Set(
        [variant.option1, variant.option2, variant.option3, variant.title]
          .filter(Boolean)
          .map((value) => String(value).replace(/\s+/g, ' ').trim())
          .filter(Boolean)
      )
    );
  }

  function sortPackages(left, right) {
    if (left.durationSort !== right.durationSort) {
      return left.durationSort - right.durationSort;
    }

    if (left.dataSort !== right.dataSort) {
      return left.dataSort - right.dataSort;
    }

    return left.priceCents - right.priceCents;
  }

  function normalizeDestinationTitle(title) {
    return String(title || '')
      .replace(/\s+Unlimited\s+eSIM$/i, '')
      .replace(/\s+Unlimited$/i, '')
      .replace(/\s+eSIM$/i, '')
      .trim();
  }

  function isGenericRegionOverview(text, destinationTitle) {
    const normalizedText = normalizeText(text);
    if (!normalizedText || !destinationTitle) return false;
    if (normalizedText.toLowerCase().includes(destinationTitle.toLowerCase())) return false;

    return getGenericRegionLabels().some((region) => {
      const regionPattern = new RegExp(`\\b${escapeRegExp(region)}\\b`, 'i');
      return regionPattern.test(normalizedText);
    });
  }

  function isGenericRegionLabel(value) {
    const normalized = normalizeText(value).toLowerCase();
    return getGenericRegionLabels().some((region) => region.toLowerCase() === normalized);
  }

  function getGenericRegionLabels() {
    return ['Africa', 'Asia', 'Europe', 'North America', 'South America', 'Latin America', 'Middle East', 'Oceania', 'Caribbean', 'Global'];
  }

  function formatDays(value) {
    return `${value} Day${value === 1 ? '' : 's'}`;
  }

  function normalizeText(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function formatMoney(cents, moneyFormat) {
    const amount = Number(cents || 0) / 100;
    const format = moneyFormat || '${{amount}}';

    const amountString = amount.toFixed(2);
    const amountNoDecimals = Math.round(amount).toString();
    const amountWithCommaSeparator = amountString.replace('.', ',');
    const amountNoDecimalsWithCommaSeparator = amountNoDecimals.replace('.', ',');

    return format
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimalsWithCommaSeparator)
      .replace('{{amount_with_comma_separator}}', amountWithCommaSeparator)
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount}}', amountString);
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeRegExp(value) {
    return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  return {
    createCombinedProductState,
    ensureActiveSelection,
    extractDataLabel,
    extractDuration,
    formatMoney,
    getActiveFamily,
    getPackagesForDuration,
    getPreferredPackage,
    getSelectedPackage,
    normalizeCombinedProductData,
    normalizeDetails,
    normalizeFamily,
    normalizePackage,
    renderDetailsMarkup,
    resolveInitialFamily,
    sanitizeProductDescription,
    setActiveDuration,
    setActiveFamily,
    setSelectedPackage,
  };
});
